import React, { useState } from "react";

import { Box, Card, Grid, TextField, Typography, useTheme } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { Send } from "@material-ui/icons";
import { format } from "date-fns";

import { ADMIN_USER_ID, KK_MM_AA, UserTypes } from "../../constants";
import ContentLoader from "../../layouts/TableContentLayout/components/ContentLoader";
import { IExtendedTheme } from "../../theme/default";
import { getFormattedDate } from "../../utils/getFormattedDate";
import { ActionMenu } from "../ActionMenu";
import { Avatar } from "../Avatar";
import { TablePagination } from "../TablePagination";

export enum DataTypes {
	Chat = "chat"
}

export type ChatMessage = {
	id: string;
	dataType: DataTypes;
	timeStamp: string;
	message: string;
	sendBy: string;
	isDeleted: boolean;
};

export enum WebRTCAdaptorStatus {
	INITIALIZED = "initialized",
	STATE_CHANGED = "ice_connection_state_changed",
	PUBLISH_STARTED = "publish_started",
	PUBLISH_FINISHED = "publish_finished",
	CLOSED = "closed",
	RECONNECTING = "reconnection_attempt_for_publisher",
	UPDATED_STATS = "updated_stats",
	NUMBER_VIEWERS = "number_of_viewers",
	PLAY_STARTED = "play_started",
	PLAY_FINISHED = "play_finished",
	STREAM_INFO = "stream_information",
	DATA_CHANNEL_OPENED = "data_channel_open",
	DATA_RECEIVED = "data_received",
	DATA_CHANNEL_ERROR = "data_channel_error",
	DATA_CHANNEL_CLOSED = "data_channel_closed"
}

const ChatComponent = ({
	handleSendWebRtcData,
	streamComments,
	isFetchingComments,
	updatePage,
	hasEnded,
	user,
	handleDeleteComment
}) => {
	const { firstName = "", lastName = "", id = null } = user || {};
	const [message, setMessage] = useState("");
	const isAdmin = id === ADMIN_USER_ID || user?.assignedUserTypes?.some(({ id }) => id === UserTypes.Admin) || false;

	const theme = useTheme<IExtendedTheme>();
	const handleMessageSend = async () => {
		if (hasEnded) return;
		handleSendWebRtcData(message);
		setMessage("");
	};

	return (
		<Grid item xs={12}>
			<Card>
				<Typography variant="h6">Comments</Typography>
				{isFetchingComments ? (
					<ContentLoader />
				) : (
					<Box mb={2} p={2} style={{ borderRadius: 4 }}>
						{(streamComments?.items || []).map(({ id, text, user, createdAt, isDeleting }) => {
							const isAdminUser =
								user?.id === ADMIN_USER_ID ||
								user?.assignedUserTypes?.some(({ id }) => id === UserTypes.Admin) ||
								false;
							return (
								<Box
									key={id}
									pb={2}
									display={"flex"}
									gridGap={10}
									alignItems={"center"}
									style={{ opacity: isDeleting ? 0.4 : 1 }}
								>
									<Avatar variant="rounded" fullName={user.fullName} />
									<Box display={"flex"} flexDirection={"column"}>
										<Box display={"flex"} alignItems={"center"} gridGap={10}>
											<Typography>{isAdminUser ? "ReMar Support" : `${user.firstName} ${user.lastName}`}</Typography>
											<Typography
												variant={"body1"}
												style={{ fontSize: "14px", color: alpha(theme.palette.text.primary, 0.6) }}
											>
												{getFormattedDate(new Date(createdAt))} at {format(new Date(createdAt), KK_MM_AA)}
											</Typography>
										</Box>
										<Box mt={1}>
											<Typography>{text}</Typography>
										</Box>
									</Box>
									{isAdmin && (
										<ActionMenu
											customMenuItems={[
												{
													label: "Delete",
													onClick: () => handleDeleteComment(id),
													visible: true,
													disabled: isDeleting
												}
											]}
										/>
									)}
									{isDeleting && (
										<Box width={"30px"}>
											<ContentLoader size={"1rem"} height={"30px"} />
										</Box>
									)}
								</Box>
							);
						})}
						{streamComments?.totalItems > 10 && (
							<TablePagination
								count={streamComments?.totalItems}
								page={streamComments?.page}
								onChange={(_, page) => updatePage(page)}
								rowsPerPage={streamComments?.perPage}
							/>
						)}
					</Box>
				)}

				<Box width={"100%"} display={"flex"} alignItems={"center"} gridGap={15}>
					<Avatar variant="rounded" fullName={`${firstName} ${lastName}`} />
					<TextField
						fullWidth
						value={message}
						disabled={hasEnded}
						onChange={e => setMessage(e.target.value)}
						placeholder="Type a message"
						onKeyDown={async e => {
							if (e.key === "Enter") {
								handleMessageSend();
							}
						}}
						InputProps={{
							style: {
								padding: "4px 8px",
								color: theme.palette.text.primary,
								background: theme.palette.background.default,
								borderRadius: "5px",
								height: "50px"
							},
							disableUnderline: true,
							endAdornment: (
								<Box ml={2}>
									<Send style={{ cursor: "pointer" }} onClick={handleMessageSend} />
								</Box>
							)
						}}
					/>
				</Box>
			</Card>
		</Grid>
	);
};

export default ChatComponent;
